/**
 * Phrase-list hints for Azure Speech so drug names, diagnoses, and scale names
 * are transcribed correctly. Hints only bias recognition toward these terms;
 * they never insert words that weren't spoken.
 */
import type { AIChartContext } from "@/lib/ai-chart-context";

/** Azure accepts more, but recognition quality drops past a few hundred phrases. */
const MAX_PHRASES = 500;

/** Common terms in behavioral health and primary care that general models miss. */
export const BASE_CLINICAL_PHRASES: readonly string[] = [
  // Antidepressants
  "sertraline", "Zoloft", "fluoxetine", "Prozac", "escitalopram", "Lexapro", "citalopram", "Celexa",
  "paroxetine", "Paxil", "venlafaxine", "Effexor", "desvenlafaxine", "Pristiq", "duloxetine", "Cymbalta",
  "bupropion", "Wellbutrin", "mirtazapine", "Remeron", "trazodone", "vortioxetine", "Trintellix",
  "vilazodone", "Viibryd", "amitriptyline", "nortriptyline", "esketamine", "Spravato",
  // Antipsychotics and mood stabilizers
  "aripiprazole", "Abilify", "quetiapine", "Seroquel", "olanzapine", "Zyprexa", "risperidone", "Risperdal",
  "lurasidone", "Latuda", "ziprasidone", "Geodon", "paliperidone", "Invega", "Invega Sustenna",
  "cariprazine", "Vraylar", "brexpiprazole", "Rexulti", "clozapine", "Clozaril", "haloperidol", "Haldol",
  "lumateperone", "Caplyta", "lithium", "lamotrigine", "Lamictal", "valproate", "divalproex", "Depakote",
  "carbamazepine", "oxcarbazepine", "Trileptal", "topiramate",
  // Anxiolytics, sleep, ADHD
  "buspirone", "BuSpar", "hydroxyzine", "Vistaril", "clonazepam", "Klonopin", "lorazepam", "Ativan",
  "alprazolam", "Xanax", "diazepam", "Valium", "zolpidem", "Ambien", "prazosin", "propranolol",
  "gabapentin", "pregabalin", "methylphenidate", "Concerta", "Ritalin", "Vyvanse", "lisdexamfetamine",
  "Adderall", "amphetamine salts", "atomoxetine", "Strattera", "guanfacine", "Intuniv", "clonidine",
  "viloxazine", "Qelbree",
  // Substance use treatment
  "buprenorphine", "Suboxone", "Sublocade", "naltrexone", "Vivitrol", "naloxone", "Narcan",
  "methadone", "acamprosate", "disulfiram", "varenicline", "Chantix",
  // Common primary care medications
  "metformin", "lisinopril", "losartan", "amlodipine", "hydrochlorothiazide", "atorvastatin",
  "rosuvastatin", "levothyroxine", "omeprazole", "pantoprazole", "albuterol", "montelukast",
  "semaglutide", "Ozempic", "Wegovy", "tirzepatide", "Mounjaro", "insulin glargine", "apixaban",
  "Eliquis", "warfarin", "prednisone", "ibuprofen", "acetaminophen",
  // Scales and screeners
  "PHQ-9", "PHQ-2", "GAD-7", "Columbia Suicide Severity Rating Scale", "C-SSRS", "AUDIT-C", "CAGE",
  "PCL-5", "MDQ", "ASRS", "Vanderbilt", "AIMS", "MoCA", "MMSE", "CIWA", "COWS", "Edinburgh",
  // Diagnoses and clinical terms
  "major depressive disorder", "generalized anxiety disorder", "bipolar I disorder", "bipolar II disorder",
  "schizoaffective disorder", "schizophrenia", "PTSD", "post-traumatic stress disorder", "OCD",
  "ADHD", "panic disorder", "social anxiety disorder", "borderline personality disorder",
  "opioid use disorder", "alcohol use disorder", "cannabis use disorder", "anhedonia", "akathisia",
  "tardive dyskinesia", "extrapyramidal symptoms", "serotonin syndrome", "suicidal ideation",
  "homicidal ideation", "passive suicidal ideation", "safety plan", "psychomotor retardation",
  "hypomania", "dysthymia", "euthymic", "flat affect", "tangential", "circumstantial",
  "auditory hallucinations", "paranoia", "insight and judgment", "titrate", "taper", "QTc",
  "A1C", "lipid panel", "TSH", "CBC", "CMP", "BID", "TID", "QHS", "PRN",
];

/** "Sertraline 50 mg tablet daily" → "Sertraline". */
export function medicationPhrase(entry: string): string {
  const words = entry.trim().split(/\s+/);
  const stop = words.findIndex((w) => /\d/.test(w));
  return (stop === -1 ? words : words.slice(0, stop)).slice(0, 4).join(" ");
}

/** "Major depressive disorder, recurrent (F33.1)" → "Major depressive disorder, recurrent". */
export function conditionPhrase(entry: string): string {
  return entry.replace(/\([^)]*\)/g, "").replace(/\s+/g, " ").trim();
}

/**
 * Patient-specific terms first (most likely to be said this visit), then the
 * base list. Deduplicated case-insensitively.
 */
export function buildScribePhrases(context?: AIChartContext | null): string[] {
  const patientTerms = [
    ...(context?.medications ?? []).map(medicationPhrase),
    ...(context?.conditions ?? []).map(conditionPhrase),
    ...(context?.allergies ?? []).map((a) => a.trim()),
  ];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const phrase of [...patientTerms, ...BASE_CLINICAL_PHRASES]) {
    const key = phrase.toLowerCase();
    if (phrase.length < 2 || phrase.length > 80 || seen.has(key)) continue;
    seen.add(key);
    out.push(phrase);
    if (out.length >= MAX_PHRASES) break;
  }
  return out;
}
